import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
// eslint-disable-next-line no-restricted-imports
import { Button, ProgressBar } from 'react-bootstrap';
import type {
  CreateRecordingResponse,
  UploadUrlResponse,
} from '~/types/api/recordings';
import useThumbnailRenderer from '~/App/Components/Record/use-thumbnail-renderer';
import { useUploadVideoToS3 } from '~/App/Api/S3Upload';
import Modal from '~/App/Components/Modal';
import { getThumbnailBlob } from '~/utils/thumbnail';
import type { ManualUploadProps } from './ManualUpload';
import { RecordingType } from '~/types/models';

const Video = styled.video`
  width: 350px;
  height: 350px;
`;

const VideoContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Options = styled.div`
  display: flex;
  justify-content: center;
`;

const ProgressBarContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 40px;
  margin-top: 10px;
`;

const Error = styled.div`
  width: 350px;
  text-align: center;
  border-radius: 6px;
  height: 27px;
  padding: 3px;
  background-color: #d0577d;
  color: white;
`;

const StyledModal = styled(Modal)`
  margin: 120px auto;
  width: fit-content;
  @media (max-width: 768px) {
    width: max-content !important;
    padding: unset;
  }
  > div {
    width: 403px;
    > span {
      margin-right: -22px;
    }
  }
`;
export type VideoUploadConfirmationProps = {
  isOpen: boolean;
  videoFile?: File | Blob;
  videoSource: string;
  onModalClose: () => void;
  onVideoUploadSuccess: () => void;
} & ManualUploadProps;

export default function VideoUploadConfirmation({
  isOpen,
  videoFile,
  videoSource,
  onModalClose,
  onVideoUploadSuccess,
}: VideoUploadConfirmationProps) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [thumbnail, setThumbnail] = useState('');

  const videoRef = useRef<HTMLVideoElement>();

  const { renderThumbnail } = useThumbnailRenderer({
    videoRef,
    onComplete: (originalDataUrl) => setThumbnail(originalDataUrl),
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!videoRef.current) return;

      if (videoRef.current.readyState < 2) return; //video is not yet loaded;
      renderThumbnail();

      clearInterval(intervalId);
    }, 500);

    return () => clearInterval(intervalId);
  }, []);

  const uploadVideoToS3 = useUploadVideoToS3({
    onUploadProgress: setUploadProgress,
    currentProgress: 5,
  });

  async function uploadVideo() {
    try {
      if (!videoFile) return;

      setUploadFailed(false);

      setUploadProgress(5);

      const { data: recording } = await axios.post<CreateRecordingResponse>(
        '/api/v2/recordings',
        {
          type: RecordingType.Upload,
        }
      );

      const { data: uploadUrl } = await axios.get<UploadUrlResponse>(
        `/api/v2/recordings/${recording.id}/upload-url`
      );

      const thumbnailBlob = await getThumbnailBlob(thumbnail);
      await uploadVideoToS3(uploadUrl.upload_url, videoFile);

      const formData = new FormData();
      formData.append('thumbnail', thumbnailBlob, 'thumbnail.jpg');
      formData.append('_method', 'PUT');

      await axios.post(
        `/api/v2/recordings/${recording.id}?single_upload=true`,
        formData
      );

      onVideoUploadSuccess();
    } catch (e) {
      setUploadFailed(true);
      setUploadProgress(0);
    }
  }

  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={onModalClose}
      showCloseButton
      hideActions
    >
      <VideoContainer>
        <Video src={videoSource} ref={videoRef} controls />
      </VideoContainer>
      <ProgressBarContainer>
        {uploadFailed && <Error>Failed to upload the video.</Error>}
        {uploadProgress !== 0 && (
          <ProgressBar
            active
            now={uploadProgress}
            label={`Uploading: ${uploadProgress}%`}
            style={{ width: 350 }}
          />
        )}
      </ProgressBarContainer>
      <Options>
        <Button
          bsStyle='info'
          onClick={onModalClose}
          disabled={uploadProgress > 0}
        >
          Cancel
        </Button>
        <Button
          bsStyle='primary'
          onClick={() => void uploadVideo()}
          disabled={uploadProgress > 0}
        >
          Upload
        </Button>
      </Options>
    </StyledModal>
  );
}
